import { useState, useRef } from 'react';
import DualDate from '../../components/common/DualDate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdReceiptLong, MdAdd, MdClose, MdAttachFile,
  MdSearch, MdOpenInNew, MdEdit, MdInfoOutline,
  MdCheckCircle, MdCancel, MdAccessTime, MdArchive,
  MdPerson, MdWarning, MdLock, MdBlock, MdDownload,
} from 'react-icons/md';
import { FaReceipt } from 'react-icons/fa';
import * as expSvc from '../../services/expenseService';
import { ktmThisMonth } from '../../utils/calendar';
import { downloadExpenseReport } from '../../services/reportExportService';
import { useCalendar } from '../../context/CalendarContext';
import useAuth from '../../hooks/useAuth';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CATEGORIES = [
  { value: 'rent',               label: 'Rent' },
  { value: 'electricity',        label: 'Electricity' },
  { value: 'water',              label: 'Water' },
  { value: 'internet',           label: 'Internet' },
  { value: 'equipment',          label: 'Equipment' },
  { value: 'trainer_payment',    label: 'Trainer Payment' },
  { value: 'cleaning',           label: 'Cleaning' },
  { value: 'transport',          label: 'Transport' },
  { value: 'tournament',         label: 'Tournament' },
  { value: 'repair_maintenance', label: 'Repair and Maintenance' },
  { value: 'marketing',          label: 'Marketing' },
  { value: 'office_supplies',    label: 'Office Supplies' },
  { value: 'software',           label: 'Software' },
  { value: 'other',              label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'khalti',        label: 'Khalti' },
  { value: 'esewa',         label: 'eSewa' },
  { value: 'fonepay_qr',    label: 'Fonepay QR' },
  { value: 'connectips',    label: 'ConnectIPS' },
  { value: 'other',         label: 'Other' },
];

const STATUS_CONFIG = {
  pending:   { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Awaiting Admin Approval', Icon: MdAccessTime,  hint: 'Submitted and waiting for admin to approve or reject. You will see the result here.' },
  approved:  { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200', label: 'Approved',                Icon: MdCheckCircle, hint: 'Approved by admin — this expense is confirmed and counted in club finances.' },
  rejected:  { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',   label: 'Rejected',                Icon: MdCancel,      hint: 'Admin rejected this expense. See the reason below and edit to resubmit.' },
  draft:     { bg: 'bg-gray-50',   text: 'text-gray-500',   border: 'border-gray-200',  label: 'Draft',                   Icon: MdArchive,     hint: 'Saved but not submitted yet. Edit and submit when ready.' },
  archived:  { bg: 'bg-gray-100',  text: 'text-gray-400',   border: 'border-gray-200',  label: 'Archived',                Icon: MdArchive,     hint: 'Archived and no longer active.' },
  cancelled: { bg: 'bg-gray-50',   text: 'text-gray-400',   border: 'border-gray-300',  label: 'Cancelled',               Icon: MdBlock,       hint: 'Submission cancelled. Create a new expense if you need to resubmit.' },
};

const TABS = [
  { key: '',          label: 'All Expenses'      },
  { key: 'mine',      label: 'My Submissions'    },
  { key: 'pending',   label: 'Awaiting Approval' },
  { key: 'approved',  label: 'Approved'          },
  { key: 'rejected',  label: 'Rejected'          },
  { key: 'cancelled', label: 'Cancelled'         },
];

const catLabel    = (v) => CATEGORIES.find(c => c.value === v)?.label    || v;
const methodLabel = (v) => PAYMENT_METHODS.find(m => m.value === v)?.label || v;

// Handles populated object { _id/id } or raw string ID from MongoDB
const getId = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (val._id) return String(val._id);
  if (val.id)  return String(val.id);
  return '';
};
const fmtNPR      = (n) => `NPR ${Number(n || 0).toLocaleString()}`;
const THIS_MONTH  = ktmThisMonth(); // Kathmandu month, not UTC

function StatusBadge({ status }) {
  const s = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
      <Icon className="text-sm" /> {s.label}
    </span>
  );
}

function AttachmentThumb({ att, expenseId }) {
  const isPdf    = att.mimeType === 'application/pdf' || att.url?.endsWith('.pdf');
  const isLegacy = !att.url?.startsWith('http');
  const [loading, setLoading] = useState(null); // 'view' | 'download' | null

  const openBlob = async (mode) => {
    setLoading(mode);
    try {
      const res     = await expSvc.viewAttachment(expenseId, att._id, mode === 'download');
      const blob    = new Blob([res.data], { type: att.mimeType || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.originalName || att.filename || 'attachment';
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      } else {
        const tab = window.open('', '_blank');
        if (tab) {
          tab.location.href = blobUrl;
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
          toast.error('Popup blocked. Please allow popups for this site.');
          URL.revokeObjectURL(blobUrl);
        }
      }
    } catch {
      toast.error('Failed to load attachment.');
    } finally {
      setLoading(null);
    }
  };

  if (isLegacy) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed text-xs font-medium text-muted"
        style={{ borderColor: 'var(--color-border)' }}
        title="This receipt was stored on the old server and may no longer be accessible">
        <MdWarning className="flex-shrink-0 text-amber-400" />
        <span className="truncate max-w-[120px]">{att.originalName || att.filename}</span>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-dashed text-xs font-medium overflow-hidden"
        style={{ borderColor: 'var(--color-border)' }}>
        <button onClick={() => openBlob('view')} disabled={!!loading}
          className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50 text-muted">
          {loading === 'view' ? <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" /> : <MdAttachFile className="flex-shrink-0" />}
          <span className="truncate max-w-[80px]">{att.originalName || att.filename}</span>
        </button>
        <button onClick={() => openBlob('download')} disabled={!!loading}
          className="px-2 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50 border-l text-muted"
          style={{ borderColor: 'var(--color-border)' }}
          title="Download">
          {loading === 'download' ? <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" /> : <MdDownload className="text-sm" />}
        </button>
      </div>
    );
  }

  const imageUrl = att.url?.startsWith('http') ? att.url : `${API_BASE}${att.url?.startsWith('/') ? '' : '/'}${att.url}`;
  return (
    <a href={imageUrl} target="_blank" rel="noreferrer"
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed text-xs font-medium transition-colors hover:border-red-500/40 text-muted">
      <MdOpenInNew className="flex-shrink-0 text-[10px]" />
      <span className="truncate max-w-[120px]">{att.originalName || att.filename}</span>
    </a>
  );
}

// ── Expense Submit / Edit Form ────────────────────────────────────────────────
function ExpenseForm({ expense, onClose, onSaved }) {
  const isEdit  = !!expense?._id;
  const fileRef = useRef();
  const [files,        setFiles]        = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [submitting,   setSubmitting]   = useState(false);
  const [form, setForm] = useState({
    title:         expense?.title         || '',
    amount:        expense?.amount        || '',
    expenseDate:   expense?.expenseDate   ? expense.expenseDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    category:      expense?.category      || '',
    paymentMethod: expense?.paymentMethod || '',
    vendorName:    expense?.vendorName    || '',
    receiptNumber: expense?.receiptNumber || '',
    description:   expense?.description  || '',
    notes:         expense?.notes        || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: max 5 MB`); return false; }
      const ok = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!ok.includes(f.type)) { toast.error(`${f.name}: JPEG, PNG, WEBP, PDF only`); return false; }
      return true;
    });
    setFiles(p => [...p, ...valid]);
    setPreviewFiles(p => [...p, ...valid.map(f => ({ file: f, url: URL.createObjectURL(f) }))]);
  };

  const removeFile = (i) => {
    setFiles(p => p.filter((_, idx) => idx !== i));
    setPreviewFiles(p => { URL.revokeObjectURL(p[i]?.url); return p.filter((_, idx) => idx !== i); });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.paymentMethod || !form.amount || !form.expenseDate) {
      return toast.error('Please fill all required fields');
    }
    if (Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (isEdit) fd.append('lastKnownUpdatedAt', expense.updatedAt || '');
      files.forEach(f => fd.append('attachments', f));

      if (isEdit) {
        await expSvc.updateExpense(expense._id, fd);
        toast.success('Expense updated and resubmitted for approval');
      } else {
        await expSvc.createExpense(fd);
        toast.success('Expense submitted for approval');
      }
      onSaved();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('This expense was updated by someone else. Please refresh before saving.');
      } else {
        toast.error(err.response?.data?.message || 'Failed to save expense');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full px-3 py-2 rounded-xl border text-sm outline-none transition-colors bg-white border-gray-200 focus:border-primary text-gray-900 placeholder-gray-400';
  const lbl = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <FaReceipt className="text-primary" />
            {isEdit ? 'Edit Expense' : 'Submit Expense'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <MdClose className="text-lg" />
          </button>
        </div>

        <div className="px-5 py-3 border-b flex items-start gap-2" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <MdInfoOutline className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            Your expense will be submitted for admin review. The admin will approve or reject it. All club members can see submitted expenses.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-5 space-y-4">
          <div>
            <label className={lbl}>Title <span className="text-red-500">*</span></label>
            <input className={inp} value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Transport for tournament" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Amount (NPR) <span className="text-red-500">*</span></label>
              <input className={inp} type="number" min="1" step="0.01" value={form.amount}
                onChange={e => set('amount', e.target.value)} placeholder="0" required />
            </div>
            <div>
              <label className={lbl}>Date <span className="text-red-500">*</span></label>
              <input className={inp} type="date" value={form.expenseDate}
                onChange={e => set('expenseDate', e.target.value)} required />
            </div>
          </div>

          <div>
            <label className={lbl}>Category <span className="text-red-500">*</span></label>
            <select className={inp} value={form.category} onChange={e => set('category', e.target.value)} required>
              <option value="">Select category</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className={lbl}>Payment Method <span className="text-red-500">*</span></label>
            <select className={inp} value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)} required>
              <option value="">Select method</option>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Vendor Name</label>
              <input className={inp} value={form.vendorName} onChange={e => set('vendorName', e.target.value)}
                placeholder="e.g. Kathmandu Taxi" />
            </div>
            <div>
              <label className={lbl}>Receipt Number</label>
              <input className={inp} value={form.receiptNumber} onChange={e => set('receiptNumber', e.target.value)}
                placeholder="Optional" />
            </div>
          </div>

          <div>
            <label className={lbl}>Description</label>
            <textarea className={`${inp} resize-none`} rows={2} value={form.description}
              onChange={e => set('description', e.target.value)} placeholder="Brief description" />
          </div>

          <div>
            <label className={lbl}>Receipt Photo or File</label>
            <div
              className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors"
              style={{ borderColor: 'var(--color-border)' }}
              onClick={() => fileRef.current?.click()}>
              <MdAttachFile className="mx-auto text-2xl mb-1" style={{ color: 'var(--color-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Tap to take a photo or upload file</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-muted)', opacity: 0.6 }}>JPEG, PNG, WEBP, PDF — max 5 MB</p>
            </div>
            <input ref={fileRef} type="file" multiple
              accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
              capture="environment" onChange={handleFiles} className="hidden" />

            {previewFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {previewFiles.map((pf, i) => (
                  <div key={i} className="relative group">
                    {pf.file.type.startsWith('image/') ? (
                      <img src={pf.url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                        <MdAttachFile className="text-gray-400 text-xl" />
                      </div>
                    )}
                    <button type="button" onClick={() => removeFile(i)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <MdClose className="text-[10px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {isEdit && expense?.attachments?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs mb-1.5" style={{ color: 'var(--color-muted)' }}>Current attachments:</p>
                <div className="flex flex-wrap gap-2">
                  {expense.attachments.map(att => <AttachmentThumb key={att._id} att={att} expenseId={expense._id} />)}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60">
              {submitting ? 'Submitting...' : isEdit ? 'Update and Resubmit' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Expense Card ──────────────────────────────────────────────────────────────
function ExpenseCard({ exp, formatDate, currentUser, onEdit, onCancel }) {
  const [expanded,      setExpanded]      = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const sc            = STATUS_CONFIG[exp.status] || STATUS_CONFIG.draft;
  const isMyOwn       = getId(exp.submittedBy) === getId(currentUser);
  const hasReceipt    = (exp.attachments?.length || 0) > 0;
  const isApproved    = exp.status === 'approved';
  const isCancelled   = exp.status === 'cancelled';
  const editableStatuses = ['draft', 'pending', 'rejected'];
  const canEdit       = isMyOwn && editableStatuses.includes(exp.status);
  const canAddReceipt = isMyOwn && editableStatuses.includes(exp.status) && !hasReceipt;
  const canCancel     = isMyOwn && !['approved', 'cancelled', 'archived'].includes(exp.status);

  return (
    <div className="bg-card rounded-2xl border border-divider shadow-sm overflow-hidden transition-colors duration-150 hover:border-secondary">
      <div className="flex items-start gap-3 p-4 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-secondary truncate">{exp.title}</p>
            <StatusBadge status={exp.status} />
            {isApproved && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                <MdLock className="text-[11px]" /> Official Record
              </span>
            )}
            {!hasReceipt && exp.status === 'pending' && isMyOwn && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
                <MdWarning className="text-[11px]" /> No Receipt
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted flex-wrap">
            <span><DualDate date={exp.expenseDate} /></span>
            <span>{catLabel(exp.category)}</span>
            {exp.submittedBy?.name && (
              <span className="flex items-center gap-0.5">
                <MdPerson className="text-[11px]" />
                {isMyOwn ? 'Me' : exp.submittedBy.name}
              </span>
            )}
          </div>
        </div>
        <p className="font-bold text-secondary whitespace-nowrap">{fmtNPR(exp.amount)}</p>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-divider pt-3">
          {/* Status hint */}
          <div className={`flex items-start gap-2 rounded-xl px-3 py-2.5 ${sc.bg} border ${sc.border}`}>
            <sc.Icon className={`${sc.text} flex-shrink-0 mt-0.5`} />
            <div>
              <p className={`text-xs ${sc.text} font-medium`}>{sc.hint}</p>
              {exp.status === 'approved' && exp.approvedBy?.name && (
                <p className="text-xs text-green-600 mt-0.5">Approved by {exp.approvedBy.name}</p>
              )}
            </div>
          </div>

          {/* Rejection reason */}
          {exp.status === 'rejected' && exp.rejectionReason && (
            <div className="rounded-xl px-3 py-2.5" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
              <p className="text-xs font-semibold text-red-600 mb-0.5">Rejection Reason</p>
              <p className="text-sm text-red-700">{exp.rejectionReason}</p>
              {exp.rejectedBy?.name && (
                <p className="text-xs text-red-400 mt-1">By {exp.rejectedBy.name}</p>
              )}
            </div>
          )}

          {/* Approved: locked notice */}
          {isApproved && (
            <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 bg-green-50 border border-green-200">
              <MdLock className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700">This expense is an official financial record. It cannot be edited or deleted.</p>
            </div>
          )}

          {/* No receipt notice for own pending */}
          {canAddReceipt && (
            <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 bg-amber-50 border border-amber-200">
              <MdWarning className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">No receipt attached. Add one to strengthen this claim before the admin reviews it.</p>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ['Method', methodLabel(exp.paymentMethod)],
              ['Vendor', exp.vendorName || '—'],
              exp.receiptNumber && ['Receipt No.', exp.receiptNumber],
              exp.submittedBy?.name && ['Submitted by', isMyOwn ? `${exp.submittedBy.name} (me)` : exp.submittedBy.name],
            ].filter(Boolean).map(([label, val]) => (
              <div key={label}>
                <p className="text-muted">{label}</p>
                <p className="font-medium text-secondary capitalize">{val}</p>
              </div>
            ))}
          </div>

          {exp.description && (
            <div>
              <p className="text-xs text-muted mb-0.5">Description</p>
              <p className="text-sm text-secondary">{exp.description}</p>
            </div>
          )}

          {exp.attachments?.length > 0 && (
            <div>
              <p className="text-xs text-muted mb-1.5">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {exp.attachments.map(att => <AttachmentThumb key={att._id} att={att} expenseId={exp._id} />)}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!isCancelled && (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button onClick={() => onEdit(exp)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:text-secondary hover:bg-nav-hover transition-colors">
                  <MdEdit /> {exp.status === 'rejected' ? 'Edit and Resubmit' : 'Edit'}
                </button>
              )}
              {canAddReceipt && (
                <button onClick={() => onEdit(exp)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-amber-200 text-xs font-semibold text-amber-700 hover:bg-amber-50 transition-colors">
                  <MdAttachFile /> Add Receipt
                </button>
              )}
              {canCancel && !confirmCancel && (
                <button onClick={() => setConfirmCancel(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors ml-auto">
                  <MdBlock /> Cancel Submission
                </button>
              )}
            </div>
          )}

          {/* Read-only notice for other trainer's expenses */}
          {!isMyOwn && (
            <p className="text-xs text-muted flex items-center gap-1">
              <MdInfoOutline className="flex-shrink-0" />
              Read only — submitted by {exp.submittedBy?.name || 'another trainer'}
            </p>
          )}

          {/* Cancel confirm dialog */}
          {confirmCancel && (
            <div className="bg-card rounded-xl p-3 border border-red-200 space-y-2">
              <p className="text-xs font-semibold text-gray-700">Cancel this expense?</p>
              <p className="text-xs text-muted">This cannot be undone. The admin will see it was cancelled.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { onCancel(exp._id); setConfirmCancel(false); }}
                  className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors">
                  Confirm Cancel
                </button>
                <button
                  onClick={() => setConfirmCancel(false)}
                  className="text-xs text-muted hover:text-secondary px-3 py-1.5 rounded-lg border border-divider transition-colors">
                  Keep
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Trainer Club Expenses Page ───────────────────────────────────────────
export default function MyExpenses() {
  document.title = 'Club Expenses — TrainAxis';
  const qc         = useQueryClient();
  const { formatDate } = useCalendar();
  const { user }   = useAuth();

  const [activeTab,  setActiveTab]  = useState('');
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [month,      setMonth]      = useState(THIS_MONTH);
  const [search,     setSearch]     = useState('');

  // Build query params from active tab
  const queryParams = { month, search: search || undefined, limit: 100 };
  if (activeTab === 'mine') {
    queryParams.mine = 'true';
  } else if (['pending', 'approved', 'rejected', 'cancelled'].includes(activeTab)) {
    queryParams.status = activeTab;
  }

  const listKey    = ['club-expenses-trainer', queryParams];
  const summaryKey = ['club-expense-summary-trainer'];

  // Auto-refresh every 20 seconds while tab is visible
  useAutoRefresh([listKey, summaryKey], 20000);

  const { data, isPending } = useQuery({
    queryKey: listKey,
    queryFn:  () => expSvc.getExpenses(queryParams).then(r => r.data),
  });
  const expenses = data?.expenses || [];

  const { data: summaryData } = useQuery({
    queryKey: summaryKey,
    queryFn:  () => expSvc.getExpenseSummary().then(r => r.data),
  });
  const summary = summaryData?.summary;

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['club-expenses-trainer'] });
    qc.invalidateQueries({ queryKey: ['club-expense-summary-trainer'] });
    setShowForm(false);
    setEditTarget(null);
  };

  const cancelMutation = useMutation({
    mutationFn: (id) => expSvc.cancelExpense(id),
    onSuccess:  () => { toast.success('Expense cancelled'); invalidateAll(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to cancel expense'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
            <MdReceiptLong className="text-primary" /> Club Expenses
          </h1>
          <p className="text-sm text-muted mt-0.5">All expenses submitted in your club</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors duration-150">
          <MdAdd /> Submit Expense
        </button>
      </div>

      {/* Summary row */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'This Month',  value: fmtNPR(summary.thisMonthTotal), sub: 'approved expenses' },
            { label: 'This Year',   value: fmtNPR(summary.thisYearTotal),  sub: 'approved total'    },
            { label: 'Pending',     value: summary.pendingCount,            sub: 'awaiting approval' },
            { label: 'Approved',    value: summary.approvedCount,           sub: 'this cycle'        },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-card rounded-2xl border border-divider p-4 shadow-sm card-hover">
              <p className="text-lg font-bold text-secondary">{value}</p>
              <p className="text-xs font-medium text-secondary mt-0.5">{label}</p>
              <p className="text-[10px] text-muted">{sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-4 w-fit" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
        {TABS.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap"
            style={activeTab === tab.key
              ? { background: 'var(--color-primary)', color: '#fff' }
              : { color: 'var(--color-muted)' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Secondary filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-2 rounded-xl border border-divider text-sm bg-card text-secondary outline-none" />
        <div className="flex items-center gap-2 flex-1 min-w-[160px] px-3 py-2 rounded-xl border border-divider bg-card">
          <MdSearch className="text-muted flex-shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="flex-1 text-sm text-secondary bg-transparent outline-none placeholder-muted" />
        </div>
        <button
          onClick={() => {
            const [y, m] = month.split('-').map(Number);
            // Build the AD-month range as strings — never local-Date → toISOString (which shifts
            // the day back on browsers ahead of UTC). `new Date(y,m,0).getDate()` = days in month.
            const lastDay = new Date(y, m, 0).getDate();
            downloadExpenseReport({
              format   : 'csv',
              startDate: `${month}-01`,
              endDate  : `${month}-${String(lastDay).padStart(2, '0')}`,
            });
          }}
          title="Export expenses as CSV"
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-sm font-medium text-muted hover:text-secondary hover:bg-nav-hover transition-colors flex-shrink-0"
        >
          <MdDownload className="text-base" /> Export CSV
        </button>
      </div>

      {/* Expense list */}
      {isPending ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 animate-pulse rounded-2xl" style={{ background: 'var(--color-card)' }} />)}
        </div>
      ) : expenses.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-divider" style={{ background: 'var(--color-card)' }}>
          <MdReceiptLong className="mx-auto text-4xl mb-3" style={{ color: 'var(--color-muted)', opacity: 0.5 }} />
          <p className="text-sm font-medium text-muted">No expenses found</p>
          <p className="text-xs text-muted mt-1 opacity-70">
            {activeTab === 'mine' ? 'You have not submitted any expenses yet' : 'No expenses match these filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map(exp => (
            <ExpenseCard
              key={exp._id}
              exp={exp}
              formatDate={formatDate}
              currentUser={user}
              onEdit={e => setEditTarget(e)}
              onCancel={(id) => cancelMutation.mutate(id)}
            />
          ))}
        </div>
      )}

      {(showForm || editTarget) && (
        <ExpenseForm
          expense={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSaved={invalidateAll}
        />
      )}
    </div>
  );
}
